import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayRemove
} from "firebase/firestore";
import { db } from "../firebase";
import Spinner from "react-bootstrap/Spinner";
import { auth } from "../firebase";

const ChapterViewer = () => {
  const { id } = useParams();
  const [chapter, setChapter] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchChapter = async () => {
      try {
        const docRef = doc(db, "content", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setChapter({ id: snap.id, ...data });
          setNewTitle(data.Title);
        }
      } catch (err) {
        console.error("Error loading page:", err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchChapter();
  }, [id]);

  const handleMediaLoadStart = () => setLoadingContent(true);
  const handleMediaLoaded = () => setLoadingContent(false);

  const handleDelete = async () => {
    if (!user) return;

    const confirmed = window.confirm("Are you sure you want to delete this page?");
    if (!confirmed) return;

    try {
      const contentRef = doc(db, "content", id);
      const bookRef = doc(db, "books", user.uid);

      // Remove content reference from book
      await updateDoc(bookRef, {
        contents: arrayRemove(id)
      });

      // Delete the content document
      await deleteDoc(contentRef);

      navigate("/manage-book");
    } catch (err) {
      console.error("Error deleting page:", err.message);
      alert("Failed to delete page.");
    }
  };

  const handleRename = async () => {
    if (!user || !chapter) return;
    try {
      await updateDoc(doc(db, "content", chapter.id), {
        Title: newTitle
      });
      setChapter((prev) => ({ ...prev, Title: newTitle }));
      setEditingTitle(false);
    } catch (err) {
      alert("Failed to update title.");
    }
  };

  const renderContent = () => {
    if (!chapter) return null;

    switch (chapter.Type) {
      case "text":
        return <p style={{ whiteSpace: "pre-wrap" }}>{chapter.StoragePath}</p>;
      case "url":
        return (
          <div className="mb-3">
            <a
              href={chapter.StoragePath}
              target="_blank"
              rel="noopener noreferrer"
              className="d-block text-break"
              style={{ wordWrap: "break-word" }}
            >
              {chapter.StoragePath}
            </a>
          </div>
        );
      case "pdf":
        return (
          <>
            {loadingContent && <div className="text-center my-3"><Spinner animation="border" /></div>}
            <iframe
              src={chapter.StoragePath}
              title="PDF Viewer"
              width="100%"
              height="600px"
              onLoad={handleMediaLoaded}
              style={{ border: "none" }}
            />
          </>
        );
      case "audio":
        return (
          <>
            {loadingContent && <div className="text-center my-3"><Spinner animation="border" /></div>}
            <audio
              controls
              src={chapter.StoragePath}
              className="w-100"
              onLoadedData={handleMediaLoaded}
              onLoadStart={handleMediaLoadStart}
            />
          </>
        );
      case "video":
        return (
          <>
            {loadingContent && <div className="text-center my-3"><Spinner animation="border" /></div>}
            <video
              controls
              src={chapter.StoragePath}
              className="w-100"
              height="400"
              onLoadedData={handleMediaLoaded}
              onLoadStart={handleMediaLoadStart}
            />
          </>
        );
      case "image":
        return (
          <>
            {loadingContent && <div className="text-center my-3"><Spinner animation="border" /></div>}
            <img
              src={chapter.StoragePath}
              alt={chapter.Title}
              className="img-fluid"
              onLoad={handleMediaLoaded}
              onLoadStart={handleMediaLoadStart}
            />
          </>
        );
      default:
        return <p>Unknown content type</p>;
    }
  };

  if (loadingData) {
    return (
      <div className="container mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading page...</p>
      </div>
    );
  }

  if (!chapter) return <div className="container mt-5">Page not found.</div>;

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="flex-grow-1">
          {editingTitle ? (
            <>
              <input
                type="text"
                className="form-control mb-2"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <button className="btn btn-sm btn-success me-2" onClick={handleRename}>
                Save
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setEditingTitle(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <h2>{chapter.Title}</h2>
              <button className="btn btn-outline-secondary btn-sm mt-2" onClick={() => setEditingTitle(true)}>
                Rename
              </button>
            </>
          )}
        </div>
        <div>
          <button className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
            Delete Page
          </button>
        </div>
      </div>

      {renderContent()}

      <div className="mt-4">
        <button className="btn btn-secondary" onClick={() => navigate("/manage-book")}>
          ← Back to Book
        </button>
      </div>
    </div>
  );
};

export default ChapterViewer;
